import type { MembershipPaymentAction } from '../domain/purchase';
import {
  type CardForm,
  type CardFormFactory,
  type CardFormHandlers,
  CardFormLoadError,
} from './CardForm';

/** How a load whose code never arrived is told apart and recovered. */
export interface CardFormChunkRecovery {
  /** The load failed because the SDK's chunk could not be fetched. */
  isChunkFailure(error: unknown): boolean;
  /** Recovers such a failure; does not settle while a page reload is under way. */
  recover(): Promise<void>;
}

/**
 * Defers loading the payment SDK until a form is mounted, so it ships only
 * with Join and "Add card". One load serves every form; a failed load rejects
 * `mount` and the next form tries again. A chunk that never arrived is handed
 * to `recovery` first and then rejects with `CardFormLoadError`: the browser
 * keeps that failure, so trying again without a reload is pointless. A 3DS
 * action handed over before the form is mounted (a bank redirect answered
 * while the SDK still loads) is kept and shown once the form is up.
 */
export const lazyCardForm = (
  load: () => Promise<CardFormFactory>,
  recovery?: CardFormChunkRecovery,
): CardFormFactory => {
  let loading: Promise<CardFormFactory> | null = null;
  const loadOnce = (): Promise<CardFormFactory> => {
    loading ??= load().catch(async (error: unknown) => {
      loading = null;
      if (!recovery?.isChunkFailure(error)) {
        throw error;
      }
      await recovery.recover().catch(() => undefined);
      throw new CardFormLoadError(error);
    });
    return loading;
  };

  return () => {
    let inner: CardForm | null = null;
    let isMounted = false;
    let isDestroyed = false;
    let pendingAction: MembershipPaymentAction | null = null;

    return {
      async mount(node: HTMLElement, handlers: CardFormHandlers) {
        const create = await loadOnce();
        if (isDestroyed) {
          return;
        }
        const form = create();
        inner = form;
        await form.mount(node, handlers);
        if (isDestroyed) {
          form.destroy();
          return;
        }
        isMounted = true;
        if (pendingAction) {
          const action = pendingAction;
          pendingAction = null;
          form.handleAction(action);
        }
      },
      submit() {
        inner?.submit();
      },
      handleAction(action: MembershipPaymentAction) {
        if (inner && isMounted) {
          inner.handleAction(action);
          return;
        }
        pendingAction = action;
      },
      destroy() {
        isDestroyed = true;
        pendingAction = null;
        inner?.destroy();
        inner = null;
      },
    };
  };
};
